import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Fab from "@mui/material/Fab";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import "./footerManual.css"
import { Link } from "react-router-dom";
import { useNavigate } from 'react-router-dom';

const FooterManual = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setEmail("");
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  // Show/hide back to top button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  return (
    <div>
      {/* Back to Top Button */}
      <Fab
        onClick={scrollToTop}
        sx={{
          position: "fixed",
          bottom: "30px",
          right: "30px",
          background: "linear-gradient(135deg, #E9242A, #1E3A8A)",
          color: "white",
          zIndex: 1000,
          opacity: showBackToTop ? 1 : 0,
          visibility: showBackToTop ? "visible" : "hidden",
          transform: showBackToTop ? "translateY(0)" : "translateY(100px)",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            background: "linear-gradient(135deg, #1E3A8A, #E9242A)",
            transform: "translateY(-5px)",
            boxShadow: "0 8px 20px rgba(233, 36, 42, 0.5)"
          },
          "@media (max-width: 768px)": {
            bottom: "20px",
            right: "20px",
            width: "48px",
            height: "48px"
          }
        }}
        aria-label="scroll back to top"
      >
        <KeyboardArrowUpIcon />
      </Fab>

      {/* Main Footer Section */}
      <div className="footer-parent">
        <Box
          className="footer-container"
          sx={{
            padding: "60px 0px 40px 0px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            margin: "0 auto",
            width: "90%",
            maxWidth: "1400px",
            flexWrap: "wrap",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: "5%",
              right: "5%",
              height: "3px",
              background: "linear-gradient(90deg, #E9242A, #1E3A8A, #E9242A)",
              borderRadius: "2px"
            },
            "@media (max-width: 768px)": {
              flexDirection: "column",
              width: "95%",
              padding: "40px 0px 30px 0px",
            },
          }}
        >
          {/* First Section - Brand & Contact */}
          <Box
            className="footer-slide-one"
            sx={{
              width: { xs: "100%", md: "25%" },
              marginBottom: "30px",
              position: "relative",
            }}
          >
            {/* Animated Logo Container */}
            <Box
              sx={{
                marginBottom: "25px",
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-10px",
                  left: 0,
                  width: "60%",
                  height: "3px",
                  background: "linear-gradient(90deg, #E9242A, transparent)",
                  borderRadius: "2px"
                }
              }}
            >
              <Box
                sx={{
                  width: "180px",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05)"
                  }
                }}
                component="img"
                src="/image/footer-logo-large.svg"
                alt="Amar Enterprises"
              />
            </Box>

            <Typography
              sx={{
                color: "#FFFFFF",
                fontSize: "14px",
                lineHeight: "1.6",
                marginBottom: "20px",
              }}
            >
              Your trusted partner in quality solutions. Building relationships through excellence and innovation since our inception.
            </Typography>

            {/* Contact Items */}
            {[
              { icon: "/image/amar-home.svg", text: "Amar Enterprises Co Ltd, 1076-1 Ooaza Komizou Iwatsuki Saitama Shi Saitama-Ken -339-0003 Japan" },
              { icon: "/image/amar-email.svg", text: "sales@amar.co.jp" },
              { icon: "/image/footer-call.svg", text: "+81-48-793-3911" },
            ].map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  marginTop: "15px",
                  alignItems: "flex-start",
                  transition: "all 0.3s ease",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "rgba(30, 58, 138, 0.1)",
                    transform: "translateX(5px)"
                  }
                }}
              >
                <Box
                  component="img"
                  src={item.icon}
                  alt="Icon"
                  sx={{
                    width: "18px",
                    minWidth: "18px",
                    marginTop: "2px"
                  }}
                />
                <Typography
                  sx={{
                    color: "#F0F0F0",
                    fontSize: "13px",
                    marginLeft: "12px",
                    lineHeight: "1.5",
                  }}
                >
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Second Section - Quick Links */}
          <Box
            className="footer-slide-two"
            sx={{
              width: { xs: "100%", md: "20%" },
              marginBottom: "30px",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#FFFFFF",
                marginBottom: "25px",
                fontSize: "18px",
                fontWeight: "600",
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-8px",
                  left: 0,
                  width: "40px",
                  height: "2px",
                  backgroundColor: "#E9242A",
                  borderRadius: "2px"
                }
              }}
            >
              Quick Links
            </Typography>
            {[
              { text: "About Us", path: "/aboutUs" },
              { text: "Why Choose Us", path: "/whyChooseAmarEnterprises" },
              { text: "Our Services", path: "/ourServices" },
              { text: "Official Bank Details", path: "/officialBankDetails" },
              { text: "Contact Us", path: "/contactUs" },
              { text: "FAQs", path: "/faqs" },
            ].map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  marginTop: "15px",
                  alignItems: "center",
                  transition: "all 0.3s ease",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  "&:hover": {
                    backgroundColor: "rgba(233, 36, 42, 0.1)",
                  }
                }}
              >
                <Box
                  component="img"
                  src="/image/triangle-arrow.svg"
                  alt="Arrow Icon"
                  sx={{
                    transition: "transform 0.3s ease",
                    width: "12px",
                  }}
                />
                <Typography
                  component={Link}
                  to={item.path}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    color: "#CBCBCB",
                    fontSize: "14px",
                    marginLeft: "12px",
                    textDecoration: "none",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      color: "#E9242A",
                      transform: "translateX(3px)"
                    }
                  }}
                >
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Third Section - Information & Support */}
          <Box
            className="footer-slide-three"
            sx={{
              width: { xs: "100%", md: "25%" },
              marginBottom: "30px",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#FFFFFF",
                marginBottom: "25px",
                fontSize: "18px",
                fontWeight: "600",
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-8px",
                  left: 0,
                  width: "40px",
                  height: "2px",
                  backgroundColor: "#E9242A",
                  borderRadius: "2px"
                }
              }}
            >
              Policy
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {/* Left Column */}
              <Box sx={{ width: "48%" }}>
                {[
                  { text: "Terms & Conditions", path: "/termsandCondition" },
                  { text: "Privacy Policy", path: "/privacyPolicy" },
                  { text: "Payment Terms & Policy", path: "/securePayment" },
                ].map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      marginTop: "15px",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src="/image/triangle-arrow.svg"
                      alt="Arrow Icon"
                      sx={{ width: "10px" }}
                    />
                    <Typography
                      component={Link}
                      to={item.path}
                      sx={{
                        color: "#CBCBCB",
                        fontSize: "13px",
                        marginLeft: "10px",
                        textDecoration: "none",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          color: "#E9242A",
                        }
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Right Column */}
              <Box sx={{ width: "48%" }}>
                {[
                  { text: "Shipping Policy", path: "/shippingPolicy" },
                  { text: "Returns & Refund Policy", path: "/returnsPolicy" },
                  { text: "Disclaimer", path: "/disclaimer" },
                ].map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      marginTop: "15px",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src="/image/triangle-arrow.svg"
                      alt="Arrow Icon"
                      sx={{ width: "10px" }}
                    />
                    <Typography
                      component={Link}
                      to={item.path}
                      sx={{
                        color: "#CBCBCB",
                        fontSize: "13px",
                        marginLeft: "10px",
                        textDecoration: "none",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          color: "#E9242A",
                        }
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Business Info */}
            <Box sx={{ marginTop: "25px", padding: "15px", backgroundColor: "rgba(30, 58, 138, 0.1)", borderRadius: "8px" }}>
              <Typography sx={{ color: "#FFFFFF", fontSize: "12px", fontWeight: "600", marginBottom: "5px" }}>
                License Details:
              </Typography>
              <Typography sx={{ color: "#CBCBCB", fontSize: "11px", lineHeight: "1.4" }}>
                Secondhand Goods Car Dealer License Number : <br /> Saitama Prefectural Public Safety Commission No. : 431260012433 <br /> Qualification Registration Number: T7-0300-0202-3122
              </Typography>
            </Box>
          </Box>

          {/* Fourth Section - Newsletter & Social */}
          <Box
            className="footer-slide-four"
            sx={{
              width: { xs: "100%", md: "25%" },
              marginBottom: "30px",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#FFFFFF",
                marginBottom: "25px",
                fontSize: "18px",
                fontWeight: "600",
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-8px",
                  left: 0,
                  width: "40px",
                  height: "2px",
                  backgroundColor: "#E9242A",
                  borderRadius: "2px"
                }
              }}
            >
              Stay Updated
            </Typography>

            {/* Newsletter Subscription */}
            <Box sx={{ marginBottom: "30px" }}>
              <Typography sx={{ color: "#CBCBCB", fontSize: "14px", marginBottom: "15px" }}>
                Get the latest updates on products and promotions
              </Typography>

              {isSubscribed ? (
                <Box sx={{
                  padding: "15px",
                  backgroundColor: "rgba(46, 125, 50, 0.2)",
                  borderRadius: "8px",
                  border: "1px solid #2E7D32",
                  textAlign: "center"
                }}>
                  <Typography sx={{ color: "#4CAF50", fontSize: "14px", fontWeight: "600" }}>
                    ✓ Thank you for subscribing!
                  </Typography>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleSubscribe} sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <TextField
                    size="small"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: "4px",
                        "& fieldset": {
                          borderColor: "#E9242A",
                        },
                        "&:hover fieldset": {
                          borderColor: "#1E3A8A",
                        },
                      }
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      background: "linear-gradient(45deg, #E9242A, #1E3A8A)",
                      color: "white",
                      fontWeight: "600",
                      padding: "8px 16px",
                      "&:hover": {
                        background: "linear-gradient(45deg, #1E3A8A, #E9242A)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(233, 36, 42, 0.4)"
                      },
                      transition: "all 0.3s ease"
                    }}
                  >
                    Subscribe
                  </Button>
                </Box>
              )}
            </Box>

            {/* Business Hours */}
            <Box sx={{ marginBottom: "25px" }}>
              <Typography sx={{ color: "#FFFFFF", fontSize: "14px", fontWeight: "600", marginBottom: "10px" }}>
                Business Hours
              </Typography>
              <Typography sx={{ color: "#CBCBCB", fontSize: "12px", lineHeight: "1.6" }}>
                Mon - Fri: 9:00 AM - 6:00 PM<br />
                Saturday: 10:00 AM - 4:00 PM<br />
                Sunday: Closed
              </Typography>
            </Box>

            {/* Social Media */}
            <Box>
              <Typography sx={{ color: "#FFFFFF", fontSize: "14px", fontWeight: "600", marginBottom: "15px" }}>
                Follow Us
              </Typography>
              <Box sx={{ display: "flex", gap: "12px" }}>
                {[
                  {
                    src: "/image/footer-facebook.svg",
                    alt: "Facebook",
                    color: "#1877F2",
                    link: "https://www.facebook.com/amar.co.jp/",
                  },
                  {
                    src: "/images/instagram.jpg",
                    alt: "Instagram",
                    color: "#E4405F",
                    link: "https://www.instagram.com/amarautos/",
                  },
                  // {
                  //   src: "/image/footer-youtube.svg",
                  //   alt: "YouTube",
                  //   color: "#FF0000",
                  //   link: "#", // add YouTube link here if needed
                  // },
                ].map((social, index) => (
                  <Box
                    key={index}
                    component="a"
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "inline-flex",
                      width: "32px",
                      height: "32px",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      padding: "6px",
                      borderRadius: "6px",
                      "& img": {
                        width: "100%",
                        height: "100%",
                      },
                      "&:hover": {
                        transform: "translateY(-3px) scale(1.1)",
                        backgroundColor: social.color,
                        boxShadow: `0 4px 12px ${social.color}80`,
                      },
                    }}
                  >
                    <img src={social.src} alt={social.alt} />
                  </Box>
                ))}
              </Box>

            </Box>
          </Box>
        </Box>

        {/* Footer Bottom Section */}
        <Box
          className="footer-end"
          sx={{
            background: "linear-gradient(135deg, #0F0F0F 0%, #1A1A1A 100%)",
            borderTop: "1px solid rgba(233, 36, 42, 0.3)",
            padding: { xs: "20px 10px", sm: "20px 5%" },
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background: "linear-gradient(90deg, transparent, #E9242A, transparent)",
            }
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              maxWidth: "1400px",
              margin: "0 auto",
              gap: { xs: "20px", sm: "0" }
            }}
          >
            {/* Copyright & Trust Seals */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
              <Box
                component="img"
                src="/image/footer-logo-small.svg"
                alt="Amar Enterprises"
                sx={{
                  width: "35px",
                  height: "auto",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "rotate(15deg)"
                  }
                }}
              />
              <Box>
                <Typography
                  sx={{
                    color: "#CBCBCB",
                    fontSize: "12px",
                    fontWeight: "500",
                  }}
                >
                  © 2026 All rights reserved by{" "}
                  <a
                    href="https://amar.co.jp/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#E9242A",
                      fontWeight: "600",
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                  >
                    AMAR ENTERPRISES CO. LTD.
                  </a>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </div>
    </div>
  )
}

export default FooterManual;